import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useSearchParams, useNavigate, Outlet } from 'react-router-dom';

import CompleteRegistration from '../pages/complete-registration';
import { AutoPayLink } from '../pages/contracts/AutoPayLink';
import ElloMakePayments from '../pages/ello-make-payments';
import { Login } from '../pages/login/login';
import { MakePaymentLink } from '../pages/make-payment/MakePaymentLink';
import { UpgradeAnnouncement } from '../pages/migration/upgradeAnnouncement';
import { OpenInvoicesLink } from '../pages/payments/OpenInvoicesLink';
import { SignUp } from '../pages/registration';
import { SelfRegistration } from '../pages/self-registration';
import { isUserAlreadyMigrated } from '../utils/migration-cookies';
import { isElloPaymentLinkContext, NavigationConfig } from '../utils/navigation-constants';

const isCompleteRegistrationPath = (pathname: string): boolean => {
    return /^\/complete-registration\/[^/]+$/.test(pathname);
};

/**
 * Single guard for all non-authenticated routes: no migration cookies → update_portal.
 * Exceptions: update_portal (unless already migrated); cookies exist; /login when coming from update_portal/register or user is migrated; /register when coming from update_portal or login (link click).
 * Migrated users (localStorage migration cookie) never see update_portal — redirected to /login synchronously.
 * Uses useEffect on redirect so the check runs reliably on manual refresh and URL changes.
 */
const NoCookieGuard: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const pathname = location.pathname;
    const hasMigrationCookies = isUserAlreadyMigrated();
    const forceMigrationFromRegistration =
        (location.state as { forceMigrationFromRegistration?: boolean } | null)?.forceMigrationFromRegistration ===
        true;
    const isUpdatePortal = pathname === NavigationConfig.UpgradeAnnouncement;
    const isElloPublicPaymentFlow = isElloPaymentLinkContext(pathname, location.search);
    const isCompleteRegistrationFlow = isCompleteRegistrationPath(pathname);
    const isSelfRegistrationRoute = pathname === NavigationConfig.SelfRegistration;
    const isLoginRoute = pathname === NavigationConfig.Login;
    const isLoginAllowedFromPortalOrRegister =
        isLoginRoute &&
        (searchParams.get('source') === 'update_portal' ||
            (location.state as { fromUpdatePortal?: boolean } | null)?.fromUpdatePortal === true ||
            searchParams.get('source') === 'register' ||
            (location.state as { fromRegister?: boolean } | null)?.fromRegister === true ||
            // TODO: Allow login access when user is redirected from migration page
            // (e.g., isExistingUser=true response, or "Log In" button click on migration page)
            searchParams.get('source') === 'login' ||
            (location.state as { fromMigration?: boolean } | null)?.fromMigration === true ||
            (location.state as { fromCompleteRegistration?: boolean } | null)?.fromCompleteRegistration === true ||
            hasMigrationCookies);
    const isRegisterAllowedFromPortalOrLogin =
        pathname === NavigationConfig.Registration &&
        (searchParams.get('source') === 'update_portal' ||
            (location.state as { fromUpdatePortal?: boolean } | null)?.fromUpdatePortal === true ||
            searchParams.get('source') === 'login' ||
            (location.state as { fromLogin?: boolean } | null)?.fromLogin === true);

    const shouldRedirectToUpdatePortal =
        !isUpdatePortal &&
        !isElloPublicPaymentFlow &&
        !isCompleteRegistrationFlow &&
        !isSelfRegistrationRoute &&
        !hasMigrationCookies &&
        !isLoginAllowedFromPortalOrRegister &&
        !isRegisterAllowedFromPortalOrLogin;

    // CRITICAL FIX: Prevent access to migration page if user is already migrated
    // This handles both initial load and manual URL changes (e.g., user types migration URL after completing migration)
    useEffect(() => {
        // Priority 1: If user manually accesses migration page and is already migrated, redirect to login
        if (hasMigrationCookies && isUpdatePortal && !forceMigrationFromRegistration) {
            navigate(NavigationConfig.Login, { replace: true });
            return;
        }

        // Priority 2: If user is not migrated and not on an allowed page, force migration page
        if (shouldRedirectToUpdatePortal) {
            navigate(NavigationConfig.UpgradeAnnouncement, { replace: true });
        }
    }, [hasMigrationCookies, isUpdatePortal, forceMigrationFromRegistration, shouldRedirectToUpdatePortal, navigate]);

    // Render logic: enforce the rules
    // Rule 1: If migrated user tries to access migration page, block them
    if (hasMigrationCookies && isUpdatePortal && !forceMigrationFromRegistration) {
        return <Navigate to={NavigationConfig.Login} replace />;
    }

    // Rule 2: Allow all other allowed routes
    if (isUpdatePortal) return <Outlet />;
    if (isElloPublicPaymentFlow) return <Outlet />;
    if (isCompleteRegistrationFlow) return <Outlet />;
    if (isSelfRegistrationRoute) return <Outlet />;
    if (isLoginAllowedFromPortalOrRegister) return <Outlet />;
    if (hasMigrationCookies) return <Outlet />;
    if (isRegisterAllowedFromPortalOrLogin) return <Outlet />;

    // Fallback: redirect to migration page
    return <Navigate to={NavigationConfig.UpgradeAnnouncement} replace />;
};

// Root path default -> login; explicit source=update_portal -> migration page.
const RootRedirect: React.FC = () => {
    const [searchParams] = useSearchParams();
    const source = searchParams.get('source');

    if (source === 'update_portal') {
        return <Navigate to={NavigationConfig.UpgradeAnnouncement} replace />;
    }

    return <Navigate to={NavigationConfig.Login} replace />;
};

// /login is always reachable; migration gating is handled by NoCookieGuard on other paths.
const LoginRoute: React.FC = () => {
    return <Login />;
};

/** Unknown paths: migrated users → login; others → migration flow entry. */
const WildcardRedirect: React.FC = () => {
    if (isUserAlreadyMigrated()) {
        return <Navigate to={NavigationConfig.Login} replace />;
    }
    return <Navigate to={NavigationConfig.UpgradeAnnouncement} replace />;
};

const NonAuthenticatedRoutes: React.FC = () => {
    return (
        <Routes>
            {/* AutoPay link: accessible before login to redirect to login, then back to autopay */}
            <Route path={NavigationConfig.AutoPayLink} element={<AutoPayLink />} />
            {/* Make Payment link: accessible before login to redirect to login, then to make-payment */}
            <Route path={NavigationConfig.MakePayment} element={<MakePaymentLink />} />
            {/* Open Invoices link: accessible before login to redirect to login, then to open-invoices */}
            <Route path={NavigationConfig.OpenInvoicesLink} element={<OpenInvoicesLink />} />
            <Route element={<NoCookieGuard />}>
                <Route path="/" element={<RootRedirect />} />
                <Route path={NavigationConfig.Login} element={<LoginRoute />} />
                <Route path={NavigationConfig.Registration} element={<SignUp />} />
                <Route path={NavigationConfig.SelfRegistration} element={<SelfRegistration />} />
                <Route path={NavigationConfig.UpgradeAnnouncement} element={<UpgradeAnnouncement />} />
                <Route path={NavigationConfig.ElloMakePayments} element={<ElloMakePayments />} />
                <Route path={NavigationConfig.ElloPaymentConfirm} element={<ElloMakePayments />} />
                <Route path={NavigationConfig.CompleteRegistration} element={<CompleteRegistration />} />
                <Route path="*" element={<WildcardRedirect />} />
            </Route>
        </Routes>
    );
};

export { NonAuthenticatedRoutes };
